import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Users, 
  BookOpen, 
  Plus, 
  Search, 
  Filter,
  FileText,
  Clock,
  User,
  Building2,
  Calendar
} from 'lucide-react';
import { useStaffUserManagement } from '@/hooks/auth/useStaffUserManagement';
import AssignmentTemplatesManager from './AssignmentTemplatesManager';
import BulkAssignmentInterface from './BulkAssignmentInterface';
import CourseCategorizationFilters from './CourseCategorizationFilters';
import ScheduledAssignmentSystem from './ScheduledAssignmentSystem';
import { toast } from '@/components/ui/sonner';

interface EnhancedStaffAssignmentDashboardProps {
  users: any[];
  onAssignmentComplete?: () => void;
}

const EnhancedStaffAssignmentDashboard: React.FC<EnhancedStaffAssignmentDashboardProps> = ({
  users,
  onAssignmentComplete
}) => {
  const { bulkAssignCourses } = useStaffUserManagement();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [isAssigning, setIsAssigning] = useState(false);

  // Sample assignment templates (in real app, these would come from database)
  const assignmentTemplates = [
    {
      id: 'frontend-fundamentals',
      name: 'Frontend Fundamentals',
      description: 'Complete frontend development foundation',
      courses: [
        'html-css-fundamentals',
        'javascript-basics',
        'react-basics'
      ],
      estimatedDuration: '12 weeks',
      difficulty: 'Beginner'
    },
    {
      id: 'backend-essentials',
      name: 'Backend Essentials',
      description: 'Server-side development basics',
      courses: [
        'programming-basics',
        'sql-fundamentals',
        'node-js-basics'
      ],
      estimatedDuration: '10 weeks',
      difficulty: 'Intermediate'
    },
    {
      id: 'full-stack-developer',
      name: 'Full Stack Developer',
      description: 'Complete full-stack development path',
      courses: [
        'html-css-fundamentals',
        'javascript-basics',
        'react-basics',
        'node-js-basics',
        'sql-fundamentals'
      ],
      estimatedDuration: '20 weeks',
      difficulty: 'Advanced'
    }
  ];

  const handleQuickAssign = async (templateId: string, userIds: string[]) => {
    setIsAssigning(true);
    try {
      const template = assignmentTemplates.find(t => t.id === templateId);
      if (!template) return;

      const success = await bulkAssignCourses(userIds, template.courses);
      if (success) {
        toast.success(`Successfully assigned ${template.name} to ${userIds.length} user(s)`);
        onAssignmentComplete?.();
      }
    } catch (error) {
      console.error('Error in quick assign:', error);
      toast.error('Failed to assign template');
    } finally {
      setIsAssigning(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || user.department === filterDepartment;
    return matchesSearch && matchesDepartment;
  });

  const getDepartments = () => {
    const departments = [...new Set(users.map(user => user.department).filter(Boolean))];
    return departments;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Enhanced Assignment Dashboard</h2>
          <p className="text-muted-foreground">
            Streamlined course assignment with templates and bulk operations
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsAssignDialogOpen(true)}>
            <FileText className="h-4 w-4 mr-2" />
            Quick Assign
          </Button>
          <Button onClick={() => setActiveTab('bulk')}>
            <Users className="h-4 w-4 mr-2" />
            Bulk Operations
          </Button>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={filterDepartment} onValueChange={setFilterDepartment}>
          <SelectTrigger className="w-48">
            <Building2 className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {getDepartments().map(dept => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Assignment Templates Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {assignmentTemplates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
            <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                {template.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {template.description}
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>{template.estimatedDuration}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <BookOpen className="h-4 w-4" />
                  <span>{template.courses.length} courses</span>
                </div>
                <Badge variant="outline">{template.difficulty}</Badge>
              </div>
              <Button 
                className="w-full mt-4" 
                variant="outline"
                onClick={() => {
                  setSelectedTemplate(template.id);
                  setIsAssignDialogOpen(true);
                }}
              >
                Quick Assign
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="bulk">Bulk Assignment</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4">
            {filteredUsers.map((user) => (
              <Card key={user.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <User className="h-8 w-8 text-primary" />
                      <div>
                        <h3 className="font-semibold">{user.name}</h3>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        {user.department && (
                          <p className="text-xs text-muted-foreground">{user.department}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{user.role || 'student'}</Badge>
                      <Badge variant={user.is_approved ? "default" : "secondary"}>
                        {user.is_approved ? "Approved" : "Pending"}
                      </Badge>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(user.id);
                          setIsAssignDialogOpen(true);
                        }}
                      >
                        Assign
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <AssignmentTemplatesManager 
            templates={assignmentTemplates}
            onQuickAssign={handleQuickAssign}
          />
        </TabsContent>

        <TabsContent value="bulk">
          <BulkAssignmentInterface 
            users={filteredUsers}
            templates={assignmentTemplates}
            onBulkAssign={handleQuickAssign}
          />
        </TabsContent>

        <TabsContent value="scheduled">
          <ScheduledAssignmentSystem 
            users={filteredUsers}
            templates={assignmentTemplates}
          />
        </TabsContent>

        <TabsContent value="categories">
          <CourseCategorizationFilters />
        </TabsContent>
      </Tabs>

      {/* Quick Assignment Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Quick Template Assignment</DialogTitle>
            <DialogDescription>
              Assign a learning template to selected user(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {!selectedUser && (
              <div className="space-y-2">
                <Label>Select User(s)</Label>
                <p className="text-sm text-muted-foreground">
                  Select users from the overview tab first
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Select Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {assignmentTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex flex-col">
                        <span>{template.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {template.courses.length} courses • {template.estimatedDuration}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (selectedTemplate && selectedUser) {
                  handleQuickAssign(selectedTemplate, [selectedUser]);
                  setIsAssignDialogOpen(false);
                  setSelectedUser('');
                  setSelectedTemplate('');
                }
              }}
              disabled={!selectedTemplate || (!selectedUser && !selectedTemplate) || isAssigning}
            >
              {isAssigning ? 'Assigning...' : 'Assign Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedStaffAssignmentDashboard;